<template>
    <form>
        <b-form-group
            label-cols="3"
            label="Confirmation:"
            label-for="confirmation"
        >
            <select
                v-model="state.confirmation.selectedOption"
                name="confirmation"
                class="form-control"
                @change="updateConfirmationMessage"
            >
                <option
                    v-for="option in state.confirmation.options"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.label }}
                </option>
            </select>
        </b-form-group>
        <b-form-group
            v-if="state.confirmation.selectedOption === 1"
            label-cols="3"
            label="Message:"
            label-for="confirmationMessage"
        >
            <content-editable
                :disabled="isLoadingConfirmationMessage"
                v-model="state.confirmation.message"
                :noHTML="false"
                tag="div"
                class="form-control"
                placeholder="Confirmation message"
                name="messageConfirmation"
                :style="{ height: confirmationTextareaHeight }"
                id="confirmationMessage"
            />
        </b-form-group>
        <b-form-group
            v-else-if="state.confirmation.selectedOption === 2"
            label-cols="3"
            label="Message:"
            label-for="confirmationMessage"
        >
            <textarea
                id="confirmationMessage"
                class="form-control"
                placeholder="Confirmation message"
                rows="4"
                v-model="state.confirmation.message"
                :style="{ height: confirmationTextareaHeight }"
                :disabled="isLoadingConfirmationMessage"
            />
        </b-form-group>
        <b-form-group
            label-cols="3"
            label="Reminder:"
            label-for="reminder"
            class="reminderSelect"
        >
            <select
                name="reminder"
                class="form-control"
                v-model="state.reminder.selectedOption"
                @change="updateReminderMessage"
            >
                <option
                    v-for="option in state.reminder.options"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.label }}
                </option>
            </select>
        </b-form-group>
        <b-form-group
            v-if="[1, 5].includes(state.reminder.selectedOption)"
            label-cols="3"
            label="Send:"
            label-for="send"
            class="sendOptions"
        >
            <div class="fields">
                <input
                    class="form-control"
                    placeholder="Eg: 1"
                    name="sendValue"
                    v-model="state.reminder.send.value"
                />
                <select
                    class="form-control"
                    v-model="state.reminder.send.periodType"
                >
                    <option value="4">Day(s)</option>
                    <option value="5">Week(s)</option>
                    <option value="6">Month(s)</option>
                    <option value="7">Year(s)</option>
                </select>
                <select
                    class="form-control"
                    v-model="state.reminder.send.option"
                >
                    <option value="1">Before Due</option>
                    <option value="2">After Due</option>
                </select>
            </div>
        </b-form-group>
        <b-form-group
            v-if="[1, 5].includes(state.reminder.selectedOption)"
            label-cols="3"
            label="Message:"
            label-for="reminderMessage"
        >
            <content-editable
                v-if="state.reminder.selectedOption === 1"
                :disabled="isLoadingReminderMessage"
                v-model="state.reminder.message"
                :noHTML="false"
                tag="div"
                class="form-control"
                placeholder="Reminder message"
                name="messageReminder"
                :style="{ height: reminderTextareaHeight }"
            />
            <textarea
                v-else-if="state.reminder.selectedOption === 5"
                id="reminderMessage"
                class="form-control"
                placeholder="Reminder message"
                rows="4"
                v-model="state.reminder.message"
                :style="{ height: reminderTextareaHeight }"
                :disabled="isLoadingReminderMessage"
            />
        </b-form-group>
    </form>
</template>

<script>
import BoardingService from "@/services/boarding.js";
import moment from "moment";
import ContentEditable from "vue-contenteditable/src/contenteditable.vue";

const CONFIRMATION = "CONFIRMATION";
const REMINDER = "REMINDER";

export default {
    components: {
        ContentEditable
    },
    data() {
        return {
            isLoadingConfirmationMessage: false,
            isLoadingReminderMessage: false,
            checkInTime: "00:00",
            checkOutTime: "00:00",
            reminderOptions: []
        };
    },
    computed: {
        client() {
            return this.$store.state.modalReservation.view.CLIENT_INFORMATION;
        },
        reservation() {
            return this.$store.state.modalReservation.view.RESERVATION;
        },
        state: {
            get() {
                return this.$store.state.modalReservation.view.CONFIRMATION;
            },
            set(value) {
                this.$store.commit("modalReservation/setViewState", {
                    view: "CONFIRMATION",
                    value: value
                });
            }
        },
        confirmationTextareaHeight() {
            let confirmationType = this.state.confirmation.selectedOption;

            switch (confirmationType) {
                case 2: // sms
                    return "125px";

                case 1: // email
                    return "300px";

                default:
                    return "120px";
            }
        },
        reminderTextareaHeight() {
            let type = this.state.reminder.selectedOption;

            switch (type) {
                case 5: // sms
                    return "125px";

                case 1: // email
                    return "300px";

                default:
                    return "120px";
            }
        },
        bookings() {
            return this.$store.state.modalReservation.bookings;
        }
    },
    async mounted() {
        let settings = await BoardingService.getSettings();
        this.initConfirmationOptions();
        this.initReminderOptions();
        this.setDefaultReminderOptions(settings);
        this.getTime(settings);
        this.updateConfirmationMessage();
        this.updateReminderMessage();
    },
    methods: {
        setDefaultReminderOptions(settings) {
            let {
                reminder_send: sendValue, // value of days/months/weeks
                reminder_send_option_id: sendOptionId, // before due, after due
                reminder_type_id: reminderDefaultId, // ie. email, sms, etc..
                time_period_id: timePeriodId // ie. days, weeks, months, years
            } = settings.reminder;
            let clientReminders = this.client.reminders || [];

            // if the client doesnt have this option in his/her reminder
            // we'll revert to 'No Reminder' option (value = 4)
            if (clientReminders.includes(reminderDefaultId)) {
                this.state.reminder.selectedOption = reminderDefaultId;
            } else {
                this.state.reminder.selectedOption = 4;
            }

            this.state.reminder.send.value = sendValue;
            this.state.reminder.send.periodType = timePeriodId;
            this.state.reminder.send.option = sendOptionId;
        },
        getTime(settings) {
            const toTimeString = (seconds) => new Date(seconds * 1000).toISOString().substring(11, 16); // prettier-ignore

            this.checkInTime = toTimeString(settings.resource.check_in);
            this.checkOutTime = toTimeString(settings.resource.check_out);
        },
        initReminderOptions() {
            let rawOptions = [];
            let options = [];
            let clientReminders = this.client.reminders || [];

            for (let reminderType in this.REMINDER_TYPE) {
                let labelMap = {
                    NO_REMINDER: "No Reminder",
                    EMAIL: "Email",
                    PHONE: "Phone",
                    SMS: "SMS (Text)",
                    MAIL: "Mail"
                };

                rawOptions.push({
                    value: this.REMINDER_TYPE[reminderType],
                    label: labelMap[reminderType]
                });
            }

            // filter by client's preferences
            for (let reminderId of clientReminders) {
                if (reminderId === 4) {
                    continue;
                }

                let reminder = rawOptions.find(r => r.value === reminderId);

                if (reminder) {
                    options.push(reminder);
                }
            }

            // no reminder is always available
            let noReminder = rawOptions.find(r => r.value === 4);
            if (noReminder) {
                options.push(noReminder);
            }

            this.state.reminder.options = options;
        },
        initConfirmationOptions() {
            let first = { value: 0, label: "No Confirmation" };
            let options = [first];
            let { email, phone } = this.client;

            if (email && email !== "") {
                options.push({ value: 1, label: "Email" });
            }

            if (phone.mobile && phone.mobile.number !== "") {
                options.push({ value: 2, label: "SMS (Text)" });
            }

            this.state.confirmation.options = options;
        },
        getProperUnixTime(date, timeString) {
            // output = date + check/in/out
            let raw = moment(date).format("YYYYMMDD ") + timeString;
            return moment(raw, "YYYYMMDD HH:mm").unix();
        },
        updateConfirmationMessage() {
            // 1 - email
            // 2 - sms
            if ([1, 2].includes(this.state.confirmation.selectedOption)) {
                this.updateMessage(CONFIRMATION);
            }
        },
        updateReminderMessage() {
            // 1 - email
            // 5 - sms
            if ([1, 5].includes(this.state.reminder.selectedOption)) {
                this.updateMessage(REMINDER);
            }
        },
        async updateMessage(type) {
            let rawValue;
            let isSplitMode = this.bookings.length > 1;
            let start, end;
            let option;
            let messageType;

            let {
                selectedPatient,
                selectedClient,
                dateCheckIn,
                dateCheckOut,
                notes
            } = this.reservation;

            if (isSplitMode) {
                let times = [];

                for (let book of this.bookings) {
                    let bookStart = this.getProperUnixTime(
                        book.start,
                        this.checkInTime
                    );
                    let bookEnd = this.getProperUnixTime(
                        book.end,
                        this.checkOutTime
                    );

                    times.push(bookStart);
                    times.push(bookEnd);
                }

                start = Math.min(...times);
                end = Math.max(...times);
            } else {
                start = this.getProperUnixTime(dateCheckIn, this.checkInTime);
                end = this.getProperUnixTime(dateCheckOut, this.checkOutTime);
            }

            // confirmation
            if (type === CONFIRMATION) {
                option = this.state.confirmation.selectedOption;
                messageType = 1;

                this.isLoadingConfirmationMessage = true;
                this.state.confirmation.message = "Loading...";
            }
            // reminder
            else if (type === REMINDER) {
                option = this.state.reminder.selectedOption;
                messageType = 0;

                this.isLoadingReminderMessage = true;
                this.state.reminder.message = "Loading...";
            } else {
                return;
            }

            let message = await BoardingService.getContentMessage({
                type: messageType,
                option: option,
                clientId: selectedClient.id,
                patientId: selectedPatient.id,
                start,
                end,
                note:notes
            });

            if (type === CONFIRMATION) {
                this.state.confirmation.message = message;
                this.isLoadingConfirmationMessage = false;
            }

            if (type === REMINDER) {
                this.state.reminder.message = message;
                this.isLoadingReminderMessage = false;
            }
        }
    }
};
</script>

<style scoped>
.reminderSelect {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e5e5e5;
}
.sendOptions .fields {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 5px;
}
</style>
